/**
 * Wizard step 2 — the metadata form (M6 §5).
 *
 * The six starred fields are the catalogue's minimum (title, domain, source, licence,
 * cadence, abstract); lineage and contact are optional because a reader who has them will
 * fill them and a reader who does not must still be able to publish. The right column is
 * read-only: whatever the automatic checks managed to read out of the file, printed only
 * where it exists (`detectedFacts`) rather than as a table of blanks.
 */
import { useTranslation } from 'react-i18next';

import type { Domain } from '../../lib/api';
import { LicenceInfo } from './LicenceInfo';
import {
  CADENCES,
  LICENCES,
  detectedFacts,
  fmtBytes,
  type MetadataForm as Form,
  type WizardState,
} from './manager';

export interface MetadataFormProps {
  /** Replace flow: the title of the dataset whose record filled this form. */
  prefilledFrom?: string | null;
  state: WizardState;
  domains: Domain[];
  onChange: (patch: Partial<Form>) => void;
}

export function MetadataForm({ state, domains, onChange, prefilledFrom = null }: MetadataFormProps) {
  const { t } = useTranslation();
  const f = state.form;
  const facts = detectedFacts(state.row?.checks_meta);

  return (
    <div className="mgr-step mgr-step-two" data-testid="mgr-step-2">
      {prefilledFrom ? (
        <p className="mgr-prefilled" data-testid="mgr-prefilled">
          {t('manager.replace.prefilled', { title: prefilledFrom })}
        </p>
      ) : null}
      <div className="mgr-form">
        <label className="mgr-field" htmlFor="mgr-f-title">
          <span className="mgr-label">
            {t('manager.f.title')} <b aria-hidden="true">*</b>
          </span>
          <input
            id="mgr-f-title"
            data-testid="mgr-f-title"
            className="mgr-input"
            required
            value={f.title}
            onChange={(e) => onChange({ title: e.target.value })}
          />
        </label>

        <label className="mgr-field" htmlFor="mgr-f-domain">
          <span className="mgr-label">
            {t('manager.f.domain')} <b aria-hidden="true">*</b>
          </span>
          <select
            id="mgr-f-domain"
            data-testid="mgr-f-domain"
            className="mgr-input"
            required
            value={f.domain}
            onChange={(e) => onChange({ domain: e.target.value })}
          >
            <option value="">{t('manager.f.choose')}</option>
            {domains.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </label>

        <label className="mgr-field" htmlFor="mgr-f-source">
          <span className="mgr-label">
            {t('manager.f.source')} <b aria-hidden="true">*</b>
          </span>
          <input
            id="mgr-f-source"
            data-testid="mgr-f-source"
            className="mgr-input"
            required
            value={f.source}
            onChange={(e) => onChange({ source: e.target.value })}
          />
        </label>

        <label className="mgr-field" htmlFor="mgr-f-licence">
          <span className="mgr-label">
            {t('manager.f.licence')} <b aria-hidden="true">*</b> <LicenceInfo />
          </span>
          <select
            id="mgr-f-licence"
            data-testid="mgr-f-licence"
            className="mgr-input"
            required
            value={f.licence}
            onChange={(e) => onChange({ licence: e.target.value })}
          >
            <option value="">{t('manager.f.choose')}</option>
            {LICENCES.map((l) => (
              // Licence names are proper names (CC-BY-4.0, ODbL) — the same string in both
              // languages, so they are not translated.
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </label>

        <label className="mgr-field" htmlFor="mgr-f-cadence">
          <span className="mgr-label">
            {t('manager.f.cadence')} <b aria-hidden="true">*</b>
          </span>
          <select
            id="mgr-f-cadence"
            data-testid="mgr-f-cadence"
            className="mgr-input"
            required
            value={f.cadence}
            onChange={(e) => onChange({ cadence: e.target.value })}
          >
            <option value="">{t('manager.f.choose')}</option>
            {CADENCES.map((c) => (
              <option key={c} value={c}>
                {t(`manager.cadence.${c}`)}
              </option>
            ))}
          </select>
        </label>

        <label className="mgr-field mgr-wide" htmlFor="mgr-f-abstract">
          <span className="mgr-label">
            {t('manager.f.abstract')} <b aria-hidden="true">*</b>
          </span>
          <textarea
            id="mgr-f-abstract"
            data-testid="mgr-f-abstract"
            className="mgr-input mgr-textarea"
            rows={3}
            required
            value={f.abstract}
            onChange={(e) => onChange({ abstract: e.target.value })}
          />
          <span className="mgr-hint">{t('manager.f.abstractHint')}</span>
        </label>

        <label className="mgr-field mgr-wide" htmlFor="mgr-f-lineage">
          <span className="mgr-label">{t('manager.f.lineage')}</span>
          <textarea
            id="mgr-f-lineage"
            data-testid="mgr-f-lineage"
            className="mgr-input mgr-textarea"
            rows={2}
            value={f.lineage}
            onChange={(e) => onChange({ lineage: e.target.value })}
          />
          <span className="mgr-hint">{t('manager.f.lineageHint')}</span>
        </label>

        <label className="mgr-field" htmlFor="mgr-f-contact">
          <span className="mgr-label">{t('manager.f.contact')}</span>
          <input
            id="mgr-f-contact"
            data-testid="mgr-f-contact"
            className="mgr-input"
            value={f.contact}
            onChange={(e) => onChange({ contact: e.target.value })}
          />
          <span className="mgr-hint">{t('manager.f.contactHint')}</span>
        </label>
      </div>

      <aside className="mgr-detected" data-testid="mgr-detected">
        <h4>{t('manager.detected.title')}</h4>
        <table className="mini">
          <tbody>
            {state.file ? (
              <tr>
                <th scope="row">{t('manager.detected.size')}</th>
                <td>{fmtBytes(state.file.size)}</td>
              </tr>
            ) : null}
            {state.row?.format ? (
              <tr>
                <th scope="row">{t('manager.detected.format')}</th>
                <td>{state.row.format}</td>
              </tr>
            ) : null}
            {facts.map((fact) => (
              <tr key={fact.key}>
                <th scope="row">{t(`manager.detected.${fact.key}`)}</th>
                <td>{fact.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {facts.length === 0 ? <p className="mgr-hint">{t('manager.detected.none')}</p> : null}
      </aside>
    </div>
  );
}
